import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox, Spin, Switch } from 'antd';
import { TPODataContext } from 'contexts/TPOContext';

import { CustomModal, MaxTagComponent } from '../../PricingSimulator/Modals/CreateEditProjectContainer';
import dayjs, { Dayjs } from 'dayjs';

//updating antd config from moment to dayjs
//antd uses moment js by default for reading value
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { antIcon } from 'components/pages/Login';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { getConfig, sendNotification } from 'utils/helperMethods';
import { queryClient } from 'services/reactQueryService';
import { DropdownItem } from 'types/tradePromoOptimization';
import { UserContext } from 'contexts/ConfigContext';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import { characterLimit, constantWidth } from './CreateEditPlanContainer';
import _ from 'lodash';
import { trackEventMixpanel } from 'utils/mixpanel';
import { TPO_PLAN_COMPARISON_APPLY_CLICKED } from 'utils/mixpanelEventNames';
//updated component that uses dayjs instead of moment
const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

type Props = {
    open: boolean;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitFinish: Function;
    data: DataType;
    disabled?: boolean;
    applyComparisonSetupLoading: boolean;
};

type DataType = {
    selectedPlanIds: number[];
};

const PlanComparisonSetupModal: FC<Props> = ({
    open,
    onCancel,
    onSubmitFinish,
    data,
    disabled = false,
    applyComparisonSetupLoading,
}) => {
    const { selectedPlanIds } = data;
    const { dimensionConfigData } = useContext(TPODataContext);
    const dim1Attribute = dimensionConfigData?.dim1Attribute;
    const dim2Attribute = dimensionConfigData?.dim2Attribute;
    const { appConfig } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const limit = tpoConfig?.attributesListLimit;
    const maxYearOfPlanSelections = tpoConfig?.maxYearOfPlanSelections ?? 2;
    const [dim2AttributeVariable, setDim2AttributesVariable] = useState<any>({});
    const [dim1AttributeVariable, setDim1AttributesVariable] = useState<any>({});
    const [plansListVariable, setPlansListVariable] = useState<any>({ limit: limit, offset: 0 });
    const [yearAgoPlanIdData, setYearAgoPlanIdData] = useState<null | number>(null);
    const [dim1Attributes, setDim1Attributes] = useState<any[]>([]);
    const [dim2Attributes, setDim2Attributes] = useState<any[]>([]);
    const [dim1SearchValue, setDim1SearchValue] = useState<string>('');
    const [dim2SearchValue, setDim2SearchValue] = useState<string>('');
    const [yearOfPlan, setYearOfPlan] = useState<number[]>([new Date().getFullYear()]);
    const [selectedBasePlan, setSelectedPlan] = useState<DropdownItem | null>(null);
    const [selectedComparisonPlans, setSelectedComparisonPlans] = useState<DropdownItem[]>([]);
    const [planListData, setPlansListData] = useState([]);
    const [includeYearAgoPlan, setIncludeYearAgoPlan] = useState<boolean>(false);
    const modalTitle = `Plan Comparison Setup`;
    const modalButtonTitle = 'Apply';

    const isSubmitButtonDisabled = validateFields();

    function validateFields() {
        if (!yearOfPlan?.length) return true;
        if (dim1Attribute && dim1Attributes?.length === 0) return true;
        if (dim2Attribute && dim2Attributes?.length === 0) return true;
        if (selectedComparisonPlans?.length === 0 && !includeYearAgoPlan) return true;
        if (!selectedBasePlan) return true;
        return false;
    }

    function resetAllFields() {
        setYearOfPlan([new Date().getFullYear()]);
        setDim1Attributes([]);
        setDim2Attributes([]);
        setDim1SearchValue('');
        setDim2SearchValue('');
        setSelectedPlan(null);
        setSelectedComparisonPlans([]);
        setPlansListData([]);
        setYearAgoPlanIdData(null);
    }

    //reset all fields to default when popup closes
    useEffect(() => {
        if (open && !selectedPlanIds?.length) {
            resetAllFields();

            //clear cache
            queryClient.removeQueries({
                exact: false,
                queryKey: ['tpo-plans-comparison'],
            });
        }
    }, [open]);

    useEffect(() => {
        setPlansListVariable({
            ...plansListVariable,
            dim1_attribute_value: dim1Attributes,
            dim2_attribute_value: dim2Attributes?.[0],
            plan_years: yearOfPlan,
        });
        // setYearAgoPlanVariable({
        //     dim1_attribute_value: dim1Attributes,
        //     dim2_attribute_value: dim2Attributes?.[0],
        //     plan_year: Number(yearOfPlan?.[0]) - 1,
        //     is_tpm_plan: true,
        // });
    }, [dim1Attributes, dim2Attributes, yearOfPlan]);

    //get attribute types - dim 1  - category
    const {
        data: dim1List,
        loading: dim1ListLoading,
        error: dim1ListError,
        count: dim1ListCount,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: open && Boolean(dim1Attribute) },
        queryKey: [
            'tpo-plans-comparison',
            'tpo-get-attribute-types',
            'tpo-get-attribute-types-dim1',
            dim1AttributeVariable,
        ],
        url: `${tradePromoOptimizationUrls.getPlanFilters}`,
        variables: { ...dim1AttributeVariable },
        parserFunction: (res: any) => {
            if (res?.data?.length === 0) {
                throw 'No Data';
            }

            const tickedAttributes: any[] = [...(dim1Attributes ?? [])];
            const result = res?.data?.map((item: any) => {
                if (item?.ticked) {
                    tickedAttributes.push(item?.label);
                }
                return {
                    label: item?.label,
                    value: item?.label,
                };
            });
            setDim1Attributes(tickedAttributes);
            //inf scroller
            if (dim1AttributeVariable?.offset > 0 && dim1List?.length > 0) return [...dim1List, ...result];
            return result;
        },
    });

    useEffect(() => {
        setDim1AttributesVariable({
            attribute_type: dim1Attribute?.key,
            limit: limit,
            offset: 0,
            filters: null,
            dimension: dim1Attribute?.dimension,
            search_string: dim1SearchValue,
        });
    }, [dim1Attribute?.key]);

    const filterObj = useMemo(() => {
        let tempObj: any = {
            // inclusion: [],
            // exclusion: [],
        };
        if (dim1Attribute?.dimension) {
            tempObj[dim1Attribute?.dimension] = { inclusion: [], exclusion: [] };
        }
        if (dim2Attribute?.dimension) {
            tempObj[dim2Attribute?.dimension] = { inclusion: [], exclusion: [] };
        }

        if (!dim1Attributes || !dim1Attribute?.key) return (tempObj = null);

        //multi select
        if (Array.isArray(dim1Attributes) && dim1Attributes.length > 0) {
            tempObj[dim1Attribute?.dimension].inclusion = dim1Attributes?.map((attr: any) => {
                return {
                    attributeType: dim1Attribute?.key,
                    attributeDesc: attr,
                    attributeExId: attr,
                    dimension: dim1Attribute?.dimension,
                };
            });
        }
        //single select
        if (typeof dim1Attributes === 'string') {
            tempObj[dim1Attribute?.dimension].inclusion = [
                {
                    attributeType: dim1Attribute?.key,
                    attributeDesc: dim1Attributes,
                    attributeExId: dim1Attributes,
                    dimension: dim1Attribute?.dimension,
                },
            ];
        }
        return tempObj;
    }, [dim1Attributes]);
    //dim2 dropdown variable

    useEffect(() => {
        setDim2AttributesVariable({
            attribute_type: dim2Attribute?.key,
            limit: limit,
            offset: 0,
            dimension: dim2Attribute?.dimension,
            filters: filterObj,
            search_string: dim2SearchValue,
        });
    }, [dim2Attribute?.key, filterObj]);

    //get attribute types - dim 2  - BU
    const {
        data: dim2List,
        loading: dim2ListLoading,
        error: dim2ListError,
        count: dim2ListCount,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: open && Boolean(dim2Attribute) },
        queryKey: [
            'tpo-plans-comparison',
            'tpo-get-attribute-types',
            'tpo-get-attribute-types-dim2',
            dim2AttributeVariable,
        ],
        url: `${tradePromoOptimizationUrls.getPlanFilters}`,
        variables: { ...dim2AttributeVariable },
        parserFunction: (res: any) => {
            if (res?.data?.length === 0) {
                throw 'No Data';
            }
            const tickedAttributes: any[] = [...(dim2Attributes ?? [])];
            const result = res?.data?.map((item: any) => {
                if (item?.ticked) {
                    tickedAttributes.push(item?.label);
                }
                return {
                    label: item?.label,
                    value: item?.label,
                };
            });
            setDim2Attributes(tickedAttributes);
            //inf scroller
            if (dim2AttributeVariable?.offset > 0 && dim2List?.length > 0) return [...dim2List, ...result];
            return result;
        },
    });

    //get distinct years
    const {
        data: distinctYearsData,
        loading: distinctYearsDataLoading,
        error: distinctYearsDataError,
        count: distinctYearsDataCount,
    } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: open },
        queryKey: ['tpo-plans-comparison', 'tpo-plan-comparison-distinct-years'],
        url: `${tradePromoOptimizationUrls.getDistinctPlanYearsUrl}`,
        variables: {},
        parserFunction: (res: any[]) => {
            if (res?.length === 0) {
                throw 'No Data';
            }

            return res;
        },
    });

    //get plans List
    const {
        loading: planListDataLoading,
        error: planListDataError,
        count: planListDataCount,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: {
            enabled: Boolean(
                open &&
                    plansListVariable?.dim1_attribute_value?.length &&
                    plansListVariable?.dim2_attribute_value?.length &&
                    plansListVariable?.plan_years?.length,
            ),
        },
        queryKey: ['tpo-plans-comparison', 'tpo-plan-comparison-plans-list', plansListVariable],
        url: `${tradePromoOptimizationUrls.getPlansListUrl}`,
        variables: { ...plansListVariable },
        parserFunction: (res: any) => {
            if (res?.plans_list?.length === 0) {
                throw 'No Data';
            }
            const result = res?.plans_list?.map((item: any) => {
                return {
                    label: item?.plan_name,
                    value: item?.plan_id,
                };
            });
            setPlansListData(result);
            return result;
        },
    });

    //get year ago tpm plan id
    // const { loading: yearAgoPlanIdDataLoading, error: yearAgoPlanIdDataError } = useCommonApiHandler({
    //     method: 'post',
    //     queryConfig: {
    //         enabled: Boolean(
    //             open &&
    //                 yearAgoPlanVariable?.dim1_attribute_value?.length &&
    //                 yearAgoPlanVariable?.dim2_attribute_value?.length &&
    //                 yearAgoPlanVariable?.plan_year,
    //         ),
    //     },
    //     queryKey: ['tpo-plans-comparison', 'tpo-plan-comparison-year-ago-plan-id', yearAgoPlanVariable],
    //     url: `${tradePromoOptimizationUrls.getPlanId}`,
    //     variables: { ...yearAgoPlanVariable },
    //     parserFunction: (res: any) => {
    //         if (!res?.plan_id) {
    //             throw 'Plan does not exist!';
    //         }

    //         setYearAgoPlanIdData(res?.plan_id);
    //         return {};
    //     },
    // });

    //list inf scroller function for dim1 nested - material group/product
    const dim1OnInfiniteScrollOnNextMemoised = useCallback(() => {
        if (dim1ListLoading) return;
        const newOffset = dim1AttributeVariable?.offset + dim1AttributeVariable?.limit;
        setDim1AttributesVariable({ ...dim1AttributeVariable, offset: newOffset });
    }, [dim1AttributeVariable, dim1ListLoading]);

    //list inf scroller function for dim1 nested - material group/product
    const dim2OnInfiniteScrollOnNextMemoised = useCallback(() => {
        if (dim2ListLoading) return;
        const newOffset = dim2AttributeVariable?.offset + dim2AttributeVariable?.limit;
        setDim2AttributesVariable({ ...dim2AttributeVariable, offset: newOffset });
    }, [dim2AttributeVariable, dim2ListLoading]);

    function handleSearch(val: string, from: 'dim1' | 'dim2') {
        if (from === 'dim1') {
            setDim1AttributesVariable({ ...dim1AttributeVariable, offset: 0, search_string: val });
        } else {
            setDim2AttributesVariable({ ...dim2AttributeVariable, offset: 0, search_string: val });
        }
    }

    //debounced search handler
    const debouncedHandleSearch = useCallback(_.debounce(handleSearch, 400), [
        dim1AttributeVariable,
        dim2AttributeVariable,
    ]);

    const onSubmit = () => {
        if (applyComparisonSetupLoading || disabled) return;

        // if (includeYearAgoPlan && !yearAgoPlanIdData && yearAgoPlanIdDataError)
        //     return sendNotification('error', yearAgoPlanIdDataError);
        const filtersToDisplay = {
            [dim1Attribute?.label ?? '']: dim1Attributes.join(', '),
            [dim2Attribute?.label ?? '']: dim2Attributes.join(', '),
            'Year of Plan': yearOfPlan,
        };
        const plansList = [
            selectedBasePlan?.value,
            ...(includeYearAgoPlan && yearAgoPlanIdData ? [yearAgoPlanIdData] : []),
            ...selectedComparisonPlans?.map((item) => item?.value),
        ];
        trackEventMixpanel(TPO_PLAN_COMPARISON_APPLY_CLICKED, {
            ...(filtersToDisplay ?? {}),
        });
        onSubmitFinish(plansList, filtersToDisplay);
    };

    return (
        <div>
            <CustomModal
                style={{ borderRadius: 8 }}
                cancelButtonProps={{ disabled: applyComparisonSetupLoading, style: { borderRadius: 6 } }}
                okButtonProps={{
                    disabled: disabled || isSubmitButtonDisabled,
                    htmlType: 'submit',
                    style: { borderRadius: 6 },
                }}
                okText={
                    applyComparisonSetupLoading ? (
                        <Spin indicator={antIcon} style={{ color: 'white' }} />
                    ) : (
                        modalButtonTitle
                    )
                }
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                width={620}
                onCancel={(e) => {
                    if (applyComparisonSetupLoading) return;
                    onCancel(e);
                }}
                title={modalTitle}
            >
                <div className="flex-col flex gap-6">
                    {dim1Attribute && (
                        <div className="flex items-center">
                            <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                <span className="text-redAlt">* </span>
                                {dim1Attribute?.label}
                                {tpoConfig?.enableMultiSelectDim1 ? '(s)' : ''}{' '}
                                {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                            </p>
                            <SelectWithInfiniteScroller
                                hasMore={dim1List?.length < dim1ListCount}
                                onInfiniteScrollOnNextMemoised={dim1OnInfiniteScrollOnNextMemoised}
                                loading={dim1ListLoading}
                                // disabled={dim1ListLoading}
                                style={{ width: constantWidth }}
                                mode={tpoConfig?.enableMultiSelectDim1 ? 'multiple' : undefined}
                                placeholder={`Select ${dim1Attribute?.label}`}
                                maxTagCount={2}
                                maxTagPlaceholder={(val) => {
                                    const remainingCount = val.length || 0;
                                    return <MaxTagComponent remainingCount={remainingCount} />;
                                }}
                                filterOption={false}
                                showArrow
                                autoClearSearchValue={false}
                                searchValue={dim1SearchValue}
                                showSearch
                                onSearch={(val) => {
                                    setDim1SearchValue(val);
                                    debouncedHandleSearch(val, 'dim1');
                                }}
                                notFoundContent={
                                    dim1ListLoading ? (
                                        <div className="p-3">
                                            <Loader />{' '}
                                        </div>
                                    ) : (
                                        <ErrorBox msg="No Data" />
                                    )
                                } // Show loader
                                maxTagTextLength={characterLimit}
                                value={dim1Attributes}
                                onChange={(val: any, item) => {
                                    const value = tpoConfig?.enableMultiSelectDim1 ? val : [val];

                                    setDim1Attributes(value);
                                    // reset other controls to default
                                    setDim2Attributes([]);
                                    setYearAgoPlanIdData(null);
                                    setIncludeYearAgoPlan(false);
                                    setSelectedPlan(null);
                                    setSelectedComparisonPlans([]);
                                }}
                                options={dim1List}
                            />
                        </div>
                    )}

                    {dim2Attribute && (
                        <div className="flex items-center">
                            <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                                <span className="text-redAlt">* </span>
                                {dim2Attribute?.label}
                                {tpoConfig?.enableMultiSelectDim2 ? '(s)' : ''}{' '}
                                {/* <QuestionCircleOutlined style={{ position: 'relative', top: -3 }} />  */}:
                            </p>
                            <SelectWithInfiniteScroller
                                hasMore={dim2List?.length < dim2ListCount}
                                onInfiniteScrollOnNextMemoised={dim2OnInfiniteScrollOnNextMemoised}
                                style={{ width: constantWidth }}
                                mode={tpoConfig?.enableMultiSelectDim2 ? 'multiple' : undefined}
                                placeholder={`Select ${dim2Attribute?.label}`}
                                maxTagCount={2}
                                maxTagPlaceholder={(val) => {
                                    const remainingCount = val.length || 0;
                                    return <MaxTagComponent remainingCount={remainingCount} />;
                                }}
                                filterOption={false}
                                showArrow
                                showSearch
                                searchValue={dim2SearchValue}
                                notFoundContent={
                                    dim2ListLoading ? (
                                        <div className="p-3">
                                            <Loader />{' '}
                                        </div>
                                    ) : (
                                        <ErrorBox msg="No Data" />
                                    )
                                } // Show loader
                                onSearch={(val) => {
                                    setDim2SearchValue(val);
                                    debouncedHandleSearch(val, 'dim2');
                                }}
                                maxTagTextLength={characterLimit}
                                value={dim2Attributes}
                                onChange={(val, item) => {
                                    const value = tpoConfig?.enableMultiSelectDim2 ? val : [val];

                                    setDim2Attributes(value);
                                    // reset other controls to default except dim1 attribute
                                    setYearAgoPlanIdData(null);
                                    setIncludeYearAgoPlan(false);
                                    setSelectedPlan(null);
                                    setSelectedComparisonPlans([]);
                                }}
                                options={dim2List}
                                loading={dim2ListLoading}
                                //disabled={dim2ListLoading}
                            />
                        </div>
                    )}
                    <div className="flex items-center">
                        <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                            <span className="text-redAlt">* </span>
                            Year of Planning :
                        </p>
                        {/* <DatePicker
                            disabled={distinctYearsDataLoading ?? distinctYearsDataError}
                            allowClear={false}
                            value={dayjs().year(yearOfPlan ?? new Date().getFullYear())}
                            style={{ width: constantWidth, borderRadius: 6 }}
                            onChange={(val, date) => {
                                setYearOfPlan(Number(date));
                            }}
                            disabledDate={(date) => {
                                //enable current year and year + 1
                                return !distinctYearsData?.includes(date.year());
                            }}
                            picker="year"
                        /> */}
                        {
                            <SelectWithInfiniteScroller
                                hasMore={false}
                                style={{ width: constantWidth }}
                                mode="multiple"
                                placeholder="Select Year of Planning"
                                maxTagCount="responsive"
                                filterOption={false}
                                showArrow
                                showSearch={false}
                                allowClear={false}
                                notFoundContent={
                                    distinctYearsDataLoading ? (
                                        <div className="p-3">
                                            <Loader />{' '}
                                        </div>
                                    ) : (
                                        <ErrorBox msg="No Data" />
                                    )
                                }
                                maxTagTextLength={8}
                                value={yearOfPlan}
                                onChange={(val: number[]) => {
                                    if (val?.length > maxYearOfPlanSelections)
                                        return sendNotification('info', 'Maximum limit exceeded');
                                    setYearOfPlan(val);
                                }}
                                options={(distinctYearsData ?? []).map((year: number) => ({
                                    label: String(year),
                                    value: year,
                                }))}
                                loading={distinctYearsDataLoading}
                                disabled={
                                    distinctYearsDataLoading ||
                                    Boolean(distinctYearsDataError) ||
                                    !distinctYearsData?.length
                                }
                            />
                        }
                    </div>
                    <div className="flex items-center">
                        <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                            <span className="text-redAlt">* </span>
                            Base Plan :
                        </p>{' '}
                        <SelectWithInfiniteScroller
                            hasMore={false}
                            //  onInfiniteScrollOnNextMemoised={dim2OnInfiniteScrollOnNextMemoised}
                            style={{ width: constantWidth }}
                            mode={undefined}
                            placeholder={`Select Base Plan`}
                            maxTagCount={2}
                            maxTagPlaceholder={(val) => {
                                const remainingCount = val.length || 0;
                                return <MaxTagComponent remainingCount={remainingCount} />;
                            }}
                            filterOption={false}
                            showArrow
                            showSearch={false}
                            // searchValue={dim2SearchValue}
                            notFoundContent={
                                planListDataLoading ? (
                                    <div className="p-3">
                                        <Loader />{' '}
                                    </div>
                                ) : (
                                    <ErrorBox msg="No Data" />
                                )
                            } // Show loader
                            // onSearch={(val) => {
                            //     setDim2SearchValue(val);
                            //     debouncedHandleSearch(val);
                            // }}
                            maxTagTextLength={8}
                            value={selectedBasePlan?.value}
                            onChange={(val, item: any) => {
                                setSelectedPlan(item);
                            }}
                            options={planListData}
                            loading={planListDataLoading}
                            disabled={!planListData?.length && !planListDataLoading}
                        />
                    </div>
                    <div className="flex items-center">
                        <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">
                            <span className="text-redAlt">* </span>
                            Comparison Plans :
                        </p>
                        <SelectWithInfiniteScroller
                            hasMore={false}
                            //  onInfiniteScrollOnNextMemoised={dim2OnInfiniteScrollOnNextMemoised}
                            style={{ width: constantWidth }}
                            mode={'multiple'}
                            placeholder={`Select Comparison Plans`}
                            maxTagCount={'responsive'}
                            // maxTagPlaceholder={(val) => {
                            //     const remainingCount = val.length || 0;
                            //     return <MaxTagComponent remainingCount={remainingCount} />;
                            // }}
                            filterOption={false}
                            showArrow
                            showSearch={false}
                            // searchValue={dim2SearchValue}
                            notFoundContent={
                                planListDataLoading ? (
                                    <div className="p-3">
                                        <Loader />{' '}
                                    </div>
                                ) : (
                                    <ErrorBox msg="No Data" />
                                )
                            } // Show loader
                            // onSearch={(val) => {
                            //     setDim2SearchValue(val);
                            //     debouncedHandleSearch(val);
                            // }}
                            maxTagTextLength={8}
                            value={selectedComparisonPlans}
                            onChange={(val, item: any) => {
                                if (item?.length > 3) return sendNotification('info', 'Maximum limit exceeded');
                                setSelectedComparisonPlans(item);
                            }}
                            options={planListData}
                            loading={planListDataLoading}
                            disabled={!planListData?.length && !planListDataLoading}
                        />
                    </div>
                    {/* <div className="flex items-center">
                        <p className="text mr-5 font-xs  w-[180px] text-right text-blackAlt">Include Year-Ago plan :</p>
                        <div className="flex items-center gap-2">
                            <span className="text font-xs text-blackAlt">No</span>
                            <Switch
                                disabled={yearAgoPlanIdDataLoading}
                                checked={includeYearAgoPlan}
                                onChange={(checked) => setIncludeYearAgoPlan(checked)}
                            />
                            <span className="text font-xs text-blackAlt">Yes</span>
                        </div>
                    </div> */}
                </div>
            </CustomModal>
        </div>
    );
};

export default PlanComparisonSetupModal;
